import { IDynamicDiplayView } from '@components/widgets/dynamic-display';
import { EnhancedDisplayType } from '@components/widgets/dynamic-display/EnhancedDisplay';
import { capitalizeFirstCharLowerOthers } from '@utils/formatting-utils';

import { PersonWidgetInfo } from '../types';

export const getPersonView = (
  data: {
    payer: PersonWidgetInfo;
    owner: PersonWidgetInfo;
    insured: PersonWidgetInfo[];
    beneficiaries: PersonWidgetInfo[];
  },
  section: 'payer' | 'owner' | 'insured' | 'beneficiary',
  isMedical = false
) => {
  const dynamicView: IDynamicDiplayView = {
    name: section,
    sections: []
  };

  if (section === 'payer' && !Array.isArray(data.payer) && data.payer.name) {
    dynamicView.sections.push({
      name: 'Payer',
      title: 'Payer',
      showActionButtons: true,
      properties: {
        Payer: {
          multiline: false,
          value: data?.payer?.name,
          name: 'Payer',
          title: 'Payer',
          tooltip: false
        },
        DateOfBirth: {
          multiline: false,
          value: data?.payer?.dateOfBirth,
          name: 'DateOfBirth',
          title: 'Date of Birth',
          tooltip: false,
          type: EnhancedDisplayType.Date,
          hidden: data?.payer.type === 'COMPANY'
        },
        PhoneType: {
          multiline: false,
          value: data?.payer?.phoneType || '',
          name: 'PhoneType',
          title: 'Phone Type',
          tooltip: false
        },
        PhoneNumber: {
          multiline: false,
          value: data?.payer?.phoneNumber || '',
          name: 'PhoneNumber',
          title: 'Phone Number',
          tooltip: false,
          type: EnhancedDisplayType.IntPhoneNumber
        },
        Nationality: {
          multiline: false,
          value: data?.payer?.nationalityOne || '',
          name: 'Nationality',
          title: 'Nationality',
          tooltip: false
        },
        IsTaxable: {
          multiline: false,
          value: data?.payer?.isTaxable ? 'Yes' : 'No',
          name: 'IsTaxable',
          title: 'Taxable',
          tooltip: false
        },
        Profession: {
          multiline: false,
          value: data?.payer?.profession || '',
          name: 'Profession',
          title: 'Profession',
          tooltip: false,
          hidden: data?.payer.type === 'COMPANY'
        },
        Address: {
          multiline: false,
          value: data?.payer?.address || '',
          name: 'Address',
          title: 'Address',
          tooltip: false
        }
      }
    });
  }

  if (section === 'payer' && !Array.isArray(data.owner) && data.owner.name) {
    dynamicView.sections.push({
      name: 'Owner',
      title: 'Owner',
      showActionButtons: false,
      properties: {
        Owner: {
          multiline: false,
          value: data?.owner?.name,
          name: 'Owner',
          title: 'Owner',
          tooltip: false
        },
        DateOfBirth: {
          multiline: false,
          value: data?.owner?.dateOfBirth,
          name: 'DateOfBirth',
          title: 'Date of Birth',
          tooltip: false,
          type: EnhancedDisplayType.Date,
          hidden: data?.owner.type === 'COMPANY'
        },
        PhoneType: {
          multiline: false,
          value: data?.owner?.phoneType || '',
          name: 'PhoneType',
          title: 'Phone Type',
          tooltip: false
        },
        PhoneNumber: {
          multiline: false,
          value: data?.owner?.phoneNumber || '',
          name: 'PhoneNumber',
          title: 'Phone Number',
          tooltip: false,
          type: EnhancedDisplayType.IntPhoneNumber
        },
        Nationality: {
          multiline: false,
          value: data?.owner?.nationalityOne || '',
          name: 'Nationality',
          title: 'Nationality',
          tooltip: false
        },
        IsTaxable: {
          multiline: false,
          value: data?.owner?.isTaxable ? 'Yes' : 'No',
          name: 'IsTaxable',
          title: 'Taxable',
          tooltip: false
        },
        Profession: {
          multiline: false,
          value: data?.owner?.profession || '',
          name: 'Profession',
          title: 'Profession',
          tooltip: false,
          hidden: data?.owner.type === 'COMPANY'
        },
        Address: {
          multiline: false,
          value: data?.owner?.address || '',
          name: 'Address',
          title: 'Address',
          tooltip: false
        }
      }
    });
  }

  if (section === 'insured' && Array.isArray(data.insured)) {
    data.insured.forEach((item, index) => {
      dynamicView.sections.push({
        name: `Insured-${index}`,
        title: index === 0 ? 'Insureds' : '',
        showActionButtons: index === 0,
        properties: {
          ['Insured' + index]: {
            multiline: false,
            value: item.name,
            name: 'Insured' + index,
            title: 'Insured',
            tooltip: false
          },
          ['Relation' + index]: {
            multiline: false,
            value: item.relation,
            name: 'Relation' + index,
            title: 'Relation',
            tooltip: false,
            hidden: !isMedical
          },
          ['DateOfBirth' + index]: {
            multiline: false,
            value: item.dateOfBirth,
            name: 'DateOfBirth' + index,
            title: 'Date of Birth',
            tooltip: false,
            type: EnhancedDisplayType.Date,
            hidden: item.type === 'COMPANY'
          },
          ['PhoneType' + index]: {
            multiline: false,
            value: item.phoneType || '',
            name: 'PhoneType' + index,
            title: 'Phone Type',
            tooltip: false
          },
          ['PhoneNumber' + index]: {
            multiline: false,
            value: item.phoneNumber || '',
            name: 'PhoneNumber' + index,
            title: 'Phone Number',
            tooltip: false,
            type: EnhancedDisplayType.IntPhoneNumber
          },
          ['Gender' + index]: {
            multiline: false,
            value: item.gender || '',
            name: 'Gender' + index,
            title: 'Gender',
            tooltip: false,
            hidden: item.type === 'COMPANY'
          },
          ['Smoker' + index]: {
            multiline: false,
            value: item.smoker ? 'Yes' : 'No',
            name: 'Smoker' + index,
            title: 'Smoker',
            tooltip: false,
            hidden: !isMedical
          },
          ['PassportNumber' + index]: {
            multiline: false,
            value: item.passportNumber || '',
            name: 'PassportNumber' + index,
            title: 'Passport Number',
            tooltip: false,
            hidden: item.type === 'COMPANY'
          },
          ['Nationality' + index]: {
            multiline: false,
            value: item.nationalityOne || '',
            name: 'Nationality' + index,
            title: 'Nationality',
            tooltip: false
          },
          ['IsTaxable' + index]: {
            multiline: false,
            value: item.isTaxable ? 'Yes' : 'No',
            name: 'IsTaxable' + index,
            title: 'Taxable',
            tooltip: false
          },
          ['OccupationClass' + index]: {
            multiline: false,
            value: item.occupationClass || '',
            name: 'OccupationClass' + index,
            title: 'Occupation Class',
            tooltip: false,
            hidden: item.type === 'COMPANY'
          },
          ['Profession' + index]: {
            multiline: false,
            value: item.profession || '',
            name: 'Profession' + index,
            title: 'Profession',
            tooltip: false,
            hidden: item.type === 'COMPANY'
          },
          ['GRLimitDays' + index]: {
            multiline: false,
            value: item.grLimitDays.toString(),
            name: 'GRLimitDays' + index,
            title: 'GR Limit Days',
            tooltip: false,
            hidden: !isMedical
          },
          ['CardNumber' + index]: {
            multiline: false,
            value: item.cardNumber || '',
            name: 'CardNumber' + index,
            title: 'Card Number',
            tooltip: false,
            hidden: !isMedical
          },
          ['Continuity' + index]: {
            multiline: false,
            value: capitalizeFirstCharLowerOthers(item.continuity) || '',
            name: 'Continuity' + index,
            title: 'Continuity',
            tooltip: false,
            hidden: !isMedical
          },
          ['TPAReference1' + index]: {
            multiline: false,
            value: item.tpaReference1 || '',
            name: 'TPAReference1' + index,
            title: 'TPA Reference 1',
            tooltip: false,
            hidden: !isMedical
          },
          ['TPAReference2' + index]: {
            multiline: false,
            value: item.tpaReference2 || '',
            name: 'TPAReference2' + index,
            title: 'TPA Reference 2',
            tooltip: false,
            hidden: !isMedical
          },
          ['Exclusions' + index]: {
            multiline: true,
            value: item.exclusions || '',
            name: 'Exclusions' + index,
            title: 'Exclusions',
            tooltip: false,
            hidden: !isMedical
          },
          ['Remarks' + index]: {
            multiline: true,
            value: item.remarks || '',
            name: 'Remarks' + index,
            title: 'Remarks',
            tooltip: false,
            hidden: !isMedical
          },
          ['Address' + index]: {
            multiline: false,
            value: item.address || '',
            name: 'Address' + index,
            title: 'Address',
            tooltip: false
          }
        }
      });
    });
  }

  if (section === 'beneficiary' && Array.isArray(data.beneficiaries)) {
    data.beneficiaries.forEach((item, index) => {
      dynamicView.sections.push({
        name: `Beneficiary-${index}`,
        title: index === 0 ? 'Beneficiaries' : '',
        showActionButtons: index === 0,
        properties: {
          ['Beneficiary' + index]: {
            multiline: false,
            value: item.name,
            name: 'Beneficiary' + index,
            title: 'Beneficiary',
            tooltip: false
          },
          ['DateOfBirth' + index]: {
            multiline: false,
            value: item.dateOfBirth,
            name: 'DateOfBirth' + index,
            title: 'Date of Birth',
            tooltip: false,
            type: EnhancedDisplayType.Date,
            hidden: item.type === 'COMPANY'
          },
          ['PhoneType' + index]: {
            multiline: false,
            value: item.phoneType || '',
            name: 'PhoneType' + index,
            title: 'Phone Type',
            tooltip: false
          },
          ['PhoneNumber' + index]: {
            multiline: false,
            value: item.phoneNumber || '',
            name: 'PhoneNumber' + index,
            title: 'Phone Number',
            tooltip: false,
            type: EnhancedDisplayType.IntPhoneNumber
          },
          ['Nationality' + index]: {
            multiline: false,
            value: item.nationalityOne || '',
            name: 'Nationality' + index,
            title: 'Nationality',
            tooltip: false
          },
          ['IsTaxable' + index]: {
            multiline: false,
            value: item.isTaxable ? 'Yes' : 'No',
            name: 'IsTaxable' + index,
            title: 'Taxable',
            tooltip: false
          },
          ['Profession' + index]: {
            multiline: false,
            value: item.profession || '',
            name: 'Profession' + index,
            title: 'Profession',
            tooltip: false,
            hidden: item.type === 'COMPANY'
          },
          ['Address' + index]: {
            multiline: false,
            value: item.address || '',
            name: 'Address' + index,
            title: 'Address',
            tooltip: false
          },
          ['Percentage' + index]: {
            multiline: false,
            value: item.beneficiaryPercentage
              ? `${item.beneficiaryPercentage} %`
              : '',
            name: 'Percentage' + index,
            title: 'Percentage',
            tooltip: false
          }
        }
      });
    });
  }

  return dynamicView;
};
